import { useState } from 'react';
import {
  bilanDeLaSemaine,
  coutDeSortie,
  semaineOrganisee,
  tourneeDuChapitre,
} from '@neopolis/engine';
import type { Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * Étape 2 — Organiser les sorties du camion.
 *
 * Un jour à la fois. Les capteurs donnent le remplissage de chaque conteneur,
 * l'îlot coche ceux à vider, et voit immédiatement ce que cette sortie coûte en
 * kilomètres. C'est la compétence C4 en actes : une donnée qui ne coûte rien à
 * produire décide d'un déplacement de plusieurs dizaines de kilomètres.
 *
 * Le remplissage affiché vient du bilan calculé par le moteur, qui déroule la
 * semaine : ce que l'îlot voit mercredi tient compte de ce qu'il a vidé lundi.
 */
export function EtapeOrganiser({
  content,
  partie,
  onOrganiser,
}: {
  content: Content;
  partie: GameState;
  onOrganiser: (jour: string, conteneurs: readonly string[]) => void;
}) {
  const tournee = tourneeDuChapitre(content, partie.chapterId);
  const [indexJour, setIndexJour] = useState(0);

  if (!tournee) return null;

  const jour = tournee.jours[Math.min(indexJour, tournee.jours.length - 1)]!;
  const choisis = partie.tournees[jour] ?? [];
  const bilan = bilanDeLaSemaine(partie, content);
  const etatDuJour = bilan.jours.find((j) => j.jour === jour);
  const cout = coutDeSortie(tournee, choisis);

  const basculer = (id: string): void => {
    const suivant = choisis.includes(id) ? choisis.filter((c) => c !== id) : [...choisis, id];
    onOrganiser(jour, suivant);
  };

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-xl font-bold text-slate-900">{tournee.titre}</h2>
        <p className="text-slate-700">{T.tournee.aide}</p>
      </header>

      <nav aria-label={T.tournee.titre} className="flex flex-wrap gap-2">
        {tournee.jours.map((candidat, index) => {
          const traite = partie.tournees[candidat] !== undefined;
          return (
            <button
              key={candidat}
              type="button"
              onClick={() => setIndexJour(index)}
              aria-pressed={candidat === jour}
              className={`min-h-touch rounded-xl px-4 font-semibold capitalize ${
                candidat === jour
                  ? 'bg-sky-600 text-white'
                  : 'bg-white text-slate-700 ring-2 ring-slate-300'
              }`}
            >
              {traite && <span aria-hidden="true">✓ </span>}
              {candidat}
            </button>
          );
        })}
      </nav>

      <ul className="space-y-2">
        {tournee.conteneurs.map((conteneur) => {
          const etat = etatDuJour?.conteneurs.find((c) => c.id === conteneur.id);
          const niveau = Math.round(etat?.remplissage ?? conteneur.remplissage_initial);
          const coche = choisis.includes(conteneur.id);
          const plein = niveau >= 100;
          const bientot = !plein && niveau >= 75;

          return (
            <li key={conteneur.id}>
              <label
                className={`flex min-h-touch cursor-pointer items-center gap-3 rounded-xl border-2 p-3 ${
                  coche ? 'border-sky-600 bg-sky-50' : 'border-slate-200 bg-white'
                }`}
              >
                <input
                  type="checkbox"
                  checked={coche}
                  onChange={() => basculer(conteneur.id)}
                  aria-label={`${T.tournee.collecter} : ${conteneur.nom}`}
                  className="size-6 accent-sky-600"
                />
                <span className="flex-1 font-semibold text-slate-900">{conteneur.nom}</span>
                <span
                  aria-label={`${T.tournee.remplissage} : ${niveau} %`}
                  className={`w-16 text-right font-mono font-bold tabular-nums ${
                    plein ? 'text-rose-700' : bientot ? 'text-amber-700' : 'text-slate-700'
                  }`}
                >
                  {niveau} %
                </span>
                <span aria-hidden="true" className="w-24">
                  <span className="block h-3 rounded-full bg-slate-200">
                    <span
                      className={`block h-3 rounded-full ${
                        plein ? 'bg-rose-500' : bientot ? 'bg-amber-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${Math.min(100, niveau)}%` }}
                    />
                  </span>
                </span>
                {(plein || bientot) && (
                  <span className="sr-only">
                    {plein ? T.tournee.deborde : T.tournee.pleinBientot}
                  </span>
                )}
              </label>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-slate-200 bg-white p-4">
        <p className="font-semibold text-slate-800">
          {choisis.length === 0 ? (
            T.tournee.camionReste
          ) : (
            <>
              {choisis.length} {T.tournee.arretsChoisis} — {T.tournee.coutDuJour} :{' '}
              <span className="tabular-nums">{Math.round(cout.km)}</span> {T.bilanCollecte.km},{' '}
              <span className="tabular-nums">{Math.round(cout.litres)}</span>{' '}
              {T.bilanCollecte.litres}
            </>
          )}
        </p>

        {/*
          Un jour non coché doit pouvoir être validé tel quel : « le camion ne
          sort pas » est une décision, et le moteur la distingue d'un jour qu'on
          n'a pas encore regardé. Sans ce bouton, elle serait inexprimable.
        */}
        <button
          type="button"
          onClick={() => {
            onOrganiser(jour, choisis);
            if (indexJour < tournee.jours.length - 1) {
              setIndexJour(indexJour + 1);
            }
          }}
          className="min-h-touch rounded-xl bg-sky-600 px-4 font-semibold text-white hover:bg-sky-700"
        >
          {indexJour < tournee.jours.length - 1 ? T.tournee.jourSuivant : T.tournee.valider}
        </button>
      </div>

      {semaineOrganisee(partie, content) && (
        <p role="status" className="font-semibold text-emerald-800">
          {T.tournee.semaineOrganisee}
        </p>
      )}
    </section>
  );
}
